import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useResourceList, useResourceMutations } from "@/hooks/use-admin-resource";
import { AdminPageHeader, RowActions, ResponsiveTableWrap, AdminEmptyState } from "@/components/admin/crud-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ImportDefaultsButton } from "@/components/admin/import-defaults-button";
import { buildFaqSeeds } from "@/lib/admin-seeds";

export const Route = createFileRoute("/_authenticated/admin/faqs")({ component: FaqsAdmin });

type Faq = { id: string; question: string; answer: string; category: string | null; sort_order: number; published: boolean };

function FaqsAdmin() {
  const { data, isLoading } = useResourceList<Faq>("faqs", "sort_order", true);
  const { create, update, remove } = useResourceMutations("faqs");
  const [editing, setEditing] = useState<Faq | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data ?? [];
    return (data ?? []).filter((f) =>
      [f.question, f.answer, f.category].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [data, search]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="FAQs"
        description="Frequently asked questions, grouped and ordered."
        onNew={() => { setEditing(null); setOpen(true); }}
        newLabel="New FAQ"
        actions={<ImportDefaultsButton table="faqs" onConflict="question" loadRows={buildFaqSeeds} />}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search questions, answers, categories…"
        count={rows.length}
        countLabel="FAQs"
      />
      {!isLoading && !rows.length ? (
        <AdminEmptyState
          title={search ? "No FAQs match your search" : "No FAQs yet"}
          description={search ? "Try a different keyword." : "Add your first FAQ to help customers self-serve."}
        />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Question</TableHead>
                  <TableHead className="hidden md:table-cell">Category</TableHead>
                  <TableHead className="hidden sm:table-cell">Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
                {rows.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-medium max-w-md">
                      <div className="truncate">{f.question}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground md:hidden">{f.category ?? "Uncategorized"}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{f.category ?? "—"}</TableCell>
                    <TableCell className="hidden sm:table-cell">{f.sort_order}</TableCell>
                    <TableCell><Badge variant={f.published ? "default" : "secondary"}>{f.published ? "Live" : "Hidden"}</Badge></TableCell>
                    <TableCell className="text-right">
                      <RowActions onEdit={() => { setEditing(f); setOpen(true); }} onDelete={() => { if (confirm("Delete this FAQ?")) remove.mutate(f.id); }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveTableWrap>
        </div>
      )}
      <FaqDialog
        key={editing?.id ?? "new"}
        open={open} onOpenChange={setOpen} editing={editing}
        onSubmit={(v: any) => {
          if (editing) update.mutate({ id: editing.id, values: v }, { onSuccess: () => setOpen(false) });
          else create.mutate(v, { onSuccess: () => setOpen(false) });
        }}
      />
    </div>
  );
}

function FaqDialog({ open, onOpenChange, editing, onSubmit }: any) {
  const [question, setQuestion] = useState(editing?.question ?? "");
  const [answer, setAnswer] = useState(editing?.answer ?? "");
  const [category, setCategory] = useState(editing?.category ?? "");
  const [order, setOrder] = useState<number>(editing?.sort_order ?? 0);
  const [published, setPublished] = useState(editing?.published ?? true);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit FAQ" : "New FAQ"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2"><Label>Question</Label><Input value={question} onChange={(e) => setQuestion(e.target.value)} /></div>
          <div className="grid gap-2"><Label>Answer</Label><Textarea rows={4} value={answer} onChange={(e) => setAnswer(e.target.value)} /></div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2"><Label>Category</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="General" /></div>
            <div className="grid gap-2"><Label>Sort order</Label><Input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} /></div>
          </div>
          <div className="flex items-center gap-3"><Switch checked={published} onCheckedChange={setPublished} /><Label>Published</Label></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({ question, answer, category: category || null, sort_order: order, published })}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
